/**
 * How a booking-link booking reads to the host (#791): the View bookings rows
 * (`pages/BookingLinks.jsx`) and the calendar notice (`EventQuickView`,
 * `CalendarEventPage`).
 *
 * E35: a row keeps its OWN zone. The meeting line, the cancelled line and the
 * "Guest declined" tooltip all render in `guest_timezone` and carry the same
 * IANA suffix, so one row never mixes two zones. A row with no
 * `guest_timezone` is shown in the viewer's zone with no label (`formatInZone`'s
 * default). The guest-facing `zoneLabelAt` label is deliberately not used here.
 */
import { formatInZone, isValidTimeZone } from './timezone';

const STAMP_FORMAT = 'MMM d, h:mm a';

/**
 * " (Pacific/Auckland)" for a row's persisted guest zone, '' when it has none.
 * The one suffix `bookingTimeText` and the cancel/decline lines share. It echoes
 * the stored string (that is what F05 pinned), so it is not a validity check.
 */
export function guestZoneSuffix(b) {
    const zone = typeof b?.guest_timezone === 'string' ? b.guest_timezone.trim() : '';
    return zone ? ` (${zone})` : '';
}

// An instant (cancelled_at, guest_response_at) in the row's zone, with the
// suffix. A zone this runtime cannot resolve formats in the viewer's zone, so
// it gets no suffix: the label must describe the clock that was used.
function stampInRowZone(b, instant) {
    const zone = typeof b?.guest_timezone === 'string' ? b.guest_timezone.trim() : '';
    const valid = isValidTimeZone(zone);
    const text = formatInZone(instant, { zone: valid ? zone : undefined, formatStr: STAMP_FORMAT });
    return text ? `${text}${valid ? guestZoneSuffix(b) : ''}` : '';
}

/** The status pill: `{ label, tone }`. Tones: `emerald` (confirmed), `stone` (cancelled, anything else). */
export function statusPill(b) {
    if (b?.status === 'confirmed') return { label: 'Confirmed', tone: 'emerald' };
    if (b?.status === 'cancelled') return { label: 'Cancelled', tone: 'stone' };
    const raw = typeof b?.status === 'string' ? b.status.trim() : '';
    return { label: raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Unknown', tone: 'stone' };
}

/** Tailwind classes per pill tone. Amber is -800, not -700: -700 sits at the 4.5:1 edge. */
export const PILL_TONE_CLASS = {
    emerald: 'bg-emerald-100 text-emerald-700',
    stone: 'bg-stone-200 text-stone-700',
    amber: 'bg-amber-100 text-amber-800',
};

/**
 * "Cancelled by guest · Oct 21, 3:12 PM (Asia/Manila)". `cancelled_by` reads
 * "by guest" / "by host" (`coach`); a legacy row with none reads
 * "Cancelled · {time}". With no `cancelled_at` the time is dropped.
 */
export function cancelledLine(b) {
    const by = b?.cancelled_by === 'guest' ? ' by guest' : b?.cancelled_by === 'coach' ? ' by host' : '';
    const when = stampInRowZone(b, b?.cancelled_at);
    return `Cancelled${by}${when ? ` · ${when}` : ''}`;
}

/** True when the guest declined the calendar invite and the booking still stands. */
export function isGuestDeclined(b) {
    return b?.guest_response === 'declined' && b?.status !== 'cancelled';
}

/** The "Guest declined" pill's tooltip: "Declined Oct 22, 9:05 AM (Asia/Manila)". */
export function declinedTitle(b) {
    const when = stampInRowZone(b, b?.guest_response_at);
    return when ? `Declined ${when}` : 'Declined';
}

/** Join Meeting is for a booking that can still be joined: never a cancelled row. */
export function showJoin(b) {
    return Boolean(b?.meet_link) && b?.status !== 'cancelled';
}

/**
 * The amber calendar notice for a booking whose guest declined the invite, or
 * '' when they did not. `event.guest_name` rides on the `bl_` chip and on the
 * provider twin; without it the copy says "The guest". The attendee list is
 * never guessed from.
 */
export function guestDeclinedText(event) {
    if (event?.guest_response !== 'declined') return '';
    const name = typeof event.guest_name === 'string' ? event.guest_name.trim() : '';
    return `${name || 'The guest'} declined this invite. The time is still held — cancel the booking if they can't come.`;
}
